import type { ReactNode } from "react";

import { Combobox } from "@alpacahq/ui/old";

import type { Country } from "~/v2/types";

type ValueAs = "name" | "alpha-3";

interface CountryComboboxProps {
  items: Country[];
  value: string | undefined;
  onChange: (value: string) => void;
  /** Fired after `onChange`, with `null` when the selection is cleared. */
  onDidSelect?: (country: Country | null) => void;
  /** Which country field the form stores. Defaults to the name. */
  valueAs?: ValueAs;
  name?: string;
  label?: string;
  placeholder?: string;
  status?: "neutral" | "error" | "success";
  errorMsg?: ReactNode;
  msgPadded?: boolean;
}

/**
 * Lowercase and fold accents (Å→a, ô→o) so ASCII queries still match.
 * NFD splits "å" into "a" + combining mark; `\p{M}` drops the marks.
 */
const fold = (value: string) =>
  value.toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");

/**
 * Also strip punctuation/spaces so "U.S." matches "US" / names containing "U.S.".
 * Letters and digits of any script are kept.
 */
const normalize = (value: string) => fold(value).replace(/[^\p{L}\p{N}]/gu, "");

/**
 * Split on punctuation so "U.S. Virgin Islands" → ["u","s","virgin","islands"].
 * Adjacent single-letter runs are also joined (`u`+`s` → `us`) so undotted
 * queries like "US Virgin Islands" match names written as "U.S.".
 */
const tokens = (value: string) => {
  const parts = fold(value)
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);

  const withJoinedInitials = [...parts];
  for (let i = 0; i < parts.length; ) {
    if (parts[i].length !== 1) {
      i += 1;
      continue;
    }
    let joined = "";
    let j = i;
    while (j < parts.length && parts[j].length === 1) {
      joined += parts[j];
      j += 1;
    }
    if (joined.length > 1) {
      withJoinedInitials.push(joined);
    }
    i = j;
  }
  return withJoinedInitials;
};

const matchRank = (rawQuery: string, country: Country) => {
  const query = normalize(rawQuery);
  const name = normalize(country.name ?? "");
  const alpha3 = normalize(country["alpha-3"] ?? "");
  const code = normalize(country["country-code"] ?? "");

  // Order-independent: "U.S. Virgin Islands" ↔ "Virgin Islands (U.S.)".
  const queryTokens = tokens(rawQuery);
  const nameTokens = new Set(tokens(country.name ?? ""));
  const tokenMatch =
    queryTokens.length > 1 &&
    queryTokens.every((token) => nameTokens.has(token));

  return [
    alpha3 === query || code === query,
    name.startsWith(query),
    alpha3.startsWith(query) || code.startsWith(query),
    name.includes(query) || tokenMatch,
    alpha3.includes(query) || code.includes(query),
  ].indexOf(true);
};

const byName = (a: Country, b: Country) =>
  (a.name ?? "").localeCompare(b.name ?? "");

/** Exported for unit tests against the real /allowed-countries payload. */
export const filterAndRank = (items: Country[], searchText: string) => {
  const query = normalize(searchText);

  if (!query) {
    return [...items].sort(byName);
  }

  return items
    .map((country) => ({ country, rank: matchRank(searchText, country) }))
    .filter(({ rank }) => rank >= 0)
    .sort((a, b) => a.rank - b.rank || byName(a.country, b.country))
    .map(({ country }) => country);
};

const storedValue = (country: Country, valueAs: ValueAs) =>
  (valueAs === "alpha-3" ? country["alpha-3"] : country.name) ?? "";

/**
 * Country picker built on the ui/old `Combobox`. Forms store a single country
 * field — the name by default, or the alpha-3 via `valueAs` — and this maps that
 * string to and from the `Country` option object in both directions.
 */
export const CountryCombobox = ({
  items,
  value,
  onChange,
  onDidSelect,
  valueAs = "name",
  name,
  label,
  placeholder = "United States of America",
  status,
  errorMsg,
  msgPadded,
}: CountryComboboxProps) => (
  <div className="[&_input]:font-medium [&_label]:text-sm" translate="no">
    <Combobox<Country>
      variant="v2"
      name={name}
      label={label}
      // Guarded on `value` so an empty form field cannot match a malformed row
      // whose stored key is also empty.
      value={
        value
          ? (items.find((country) => storedValue(country, valueAs) === value) ??
            null)
          : null
      }
      options={(searchText) => filterAndRank(items, searchText)}
      getOptionLabel={(country) => country.name ?? ""}
      getOptionKey={(country) => country["alpha-3"] ?? country.name}
      onChange={(country) => {
        onChange(country ? storedValue(country, valueAs) : "");
        onDidSelect?.(country);
      }}
      placeholder={placeholder}
      status={status}
      errorMsg={errorMsg}
      msgPadded={msgPadded}
      dropdownButton
    />
  </div>
);
